import React, { useEffect } from 'react';
import { Alert, Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { showWarning } from '../store/slices/toastSlice';
import { UserRole } from '../types';

interface RoleGuardProps {
  allowedRoles: UserRole[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

const RoleGuard = ({ 
  allowedRoles, 
  children, 
  fallback 
}: RoleGuardProps): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (!user) {
      dispatch(showWarning({
        title: 'Access Denied',
        message: 'Authentication required to access this content.',
        duration: 4000,
      }));
    } else {
      const userRole = user.role as UserRole;
      const hasAccess = allowedRoles.includes(userRole);
      
      if (!hasAccess) {
        dispatch(showWarning({
          title: 'Insufficient Permissions',
          message: `You don't have permission to access this content. Required roles: ${allowedRoles.join(', ')}`,
          duration: 5000,
        }));
      }
    }
  }, [user, allowedRoles, dispatch]);

  if (!user) {
    return (
      <Alert severity="error">
        Authentication required to access this content.
      </Alert>
    );
  }

  const userRole = user.role as UserRole;
  const hasAccess = allowedRoles.includes(userRole);

  if (!hasAccess) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to access this content. Required roles: {allowedRoles.join(', ')}
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};

export default RoleGuard;
